import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocalSearchParams, useRouter } from 'expo-router';
import Head from 'expo-router/head';
import { ActivityIndicator, Pressable, ScrollView, StyleSheet, Text, TextInput, View, useWindowDimensions, type NativeScrollEvent, type NativeSyntheticEvent } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { withCardFee, withoutCardFee } from '@suitescanner/pricing';
import { api } from '~/api';
import { openFreeCancellationBefore } from '~/lib/cancellation';
import type { HotelSummary } from '~/lib/api/types';
import { track } from '~/lib/analytics';
import { trackAmplitude } from '~/lib/analytics/amplitude';
import { formatRangeLabel, formatRangeLabelCompact, todayIso } from '~/lib/dateUtils';
import { formatGuestsLabel, guestsFromOccupancy, type GuestsSelection } from '~/lib/guests';
import { resolveImageUrl } from '~/lib/images';
import { DEFAULT_OCCUPANCY, decodeOccupancy, encodeOccupancy, type Occupancy } from '~/lib/occupancy';
import { getDefaultResidency, normalizeResidency } from '~/lib/residency';
import { useMember } from '~/stores/useMember';
import { useCurrency } from '~/stores/useCurrency';
import { convertPriceCeil, formatCurrency } from '~/lib/currency';
import { colors, control, fontFamily, maxContentWidth, radii, shadows, space, spacing, type } from '~/ui/tokens';
// Pagination is unused while results load infinitely (see loadMore).
import { Checkbox, EmptyState, ModalSheet, Pagination } from '../components/ui';
import { CityStandoutDeals, CityStandoutDealsSkeleton } from '../components/CityStandoutDeals';
import { HotelRow } from '../components/HotelRow';
import type { Place, SearchBarValue } from '../components/ReferenceSearchBar';
import { buildHotelHref, exactHotelMatch, findHotelPlace } from '~/lib/destinations';
import { NavSearchBar } from '../components/NavSearchBar';
import { Screen } from '../components/Screen';
import { SearchResultsSkeleton } from '../components/SearchResultsSkeleton';

type SortKey = 'recommended' | 'price' | 'stars';
type AmenityKey = 'pool' | 'parking' | 'wifi' | 'kitchen' | 'pets';
const SORT_KEYS: SortKey[] = ['recommended', 'price', 'stars'];
const AMENITY_KEYS = ['pool', 'parking', 'wifi', 'kitchen', 'pets'] as const;
const STAR_FILTERS = [2, 3, 4, 5];
// The supplier filters stars, price and breakfast before it limits results.
// Free cancellation, amenities, sorting and the per-filter counts could only
// cover the hotels fetched so far, so they stay hidden until it supports them.
const SHOW_FETCHED_ONLY_FILTERS = false;

// A price from the URL, kept only when it is a positive number.
function priceParam(value?: string): string {
  return value && Number(value) > 0 ? String(Number(value)) : '';
}

// Also the divisor for a result's rank across the whole result set. If the two
// ever disagree, every recorded rank is silently wrong.
const RESULTS_PER_PAGE = 24;
// Start loading the next page this far (px) before the end of the list.
const LOAD_MORE_THRESHOLD_PX = 1200;

const BASE_SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: 'recommended', label: 'Recommended' },
  { value: 'price', label: 'Price: low to high' },
  { value: 'stars', label: 'Star rating' },
];

const AMENITY_FILTERS: Array<{ key: AmenityKey; label: string; icon: 'water-outline' | 'car-outline' | 'wifi-outline' | 'restaurant-outline' | 'paw-outline'; terms: string[] }> = [
  { key: 'pool', label: 'Pool', icon: 'water-outline', terms: ['pool', 'swimming'] },
  { key: 'parking', label: 'Parking', icon: 'car-outline', terms: ['parking', 'car park'] },
  { key: 'wifi', label: 'Free Wi-Fi', icon: 'wifi-outline', terms: ['wifi', 'wi-fi', 'internet'] },
  { key: 'kitchen', label: 'Kitchen', icon: 'restaurant-outline', terms: ['kitchen', 'kitchenette'] },
  { key: 'pets', label: 'Pets allowed', icon: 'paw-outline', terms: ['pet'] },
];

function hasAmenity(hotel: HotelSummary, terms: string[]) {
  return hotel.amenities?.some((amenity) => terms.some((term) => amenity.toLowerCase().includes(term))) ?? false;
}

function paramsToSearchBarValue(params: {
  destination?: string;
  checkIn?: string;
  checkOut?: string;
  adults?: string;
  children?: string;
  rooms?: string;
  region?: string;
  checkin?: string;
  checkout?: string;
  occ?: string;
  residency?: string;
}): SearchBarValue {
  const targetOccupancy = params.occ ? decodeOccupancy(params.occ) : DEFAULT_OCCUPANCY;
  return {
    destination: params.destination ?? params.region ?? '',
    residency: params.residency === undefined ? getDefaultResidency() : normalizeResidency(params.residency) || getDefaultResidency(),
    dates: { start: params.checkIn || params.checkin || null, end: params.checkOut || params.checkout || null },
    guests: params.occ
      ? guestsFromOccupancy(targetOccupancy)
      : {
          adults: Number(params.adults) || 1,
          children: Number(params.children) || 0,
          rooms: Number(params.rooms) || 1,
        },
  };
}

function leadTimeDaysFrom(checkin: string | null): number {
  if (!checkin) return 0;
  const diff = new Date(`${checkin}T00:00:00Z`).getTime() - new Date(`${todayIso()}T00:00:00Z`).getTime();
  return Math.round(diff / 86_400_000);
}

// The contract's neutral 'rank' name for the app's default ordering, kept
// distinct from the app's own "recommended" label copy.
function toContractSort(sort: SortKey): 'rank' | 'price' | 'stars' {
  return sort === 'recommended' ? 'rank' : sort;
}

type PendingFilterChange = {
  filterType: 'minStars' | 'minPrice' | 'maxPrice' | 'freeCancellation' | 'breakfast' | 'amenities';
  value: string;
  countBefore: number;
};

function toEtgGuests(guests: GuestsSelection): Occupancy {
  if (guests.occupancy?.length) return guests.occupancy;
  const rooms = Math.max(1, Math.min(3, guests.rooms));
  const baseAdults = Math.max(1, Math.floor(guests.adults / rooms));
  return Array.from({ length: rooms }, (_, index) => ({
    adults: baseAdults + (index < guests.adults % rooms ? 1 : 0),
    children: index === 0 ? Array.from({ length: guests.children }, () => 8) : [],
  }));
}

function toHotelSummary(hotel: Api.Search.Hotels.Result): HotelSummary {
  const price = {
    amount: String(withCardFee(hotel.nightlyFrom?.amount ?? 0)),
    currencyCode: hotel.nightlyFrom?.currency ?? 'USD',
  };
  return {
    id: hotel.id,
    hid: hotel.hid,
    name: hotel.name,
    starRating: hotel.starRating,
    reviewScore: typeof hotel.rating === 'number' && hotel.rating > 0 ? hotel.rating : null,
    reviewCount: typeof hotel.reviewCount === 'number' ? hotel.reviewCount : null,
    thumbnail: hotel.image ? { uri: resolveImageUrl(hotel.image, '640x400') } : null,
    images: (hotel.images ?? []).map((image) => ({ uri: resolveImageUrl(image, '640x400') })),
    address: hotel.address ?? '',
    lowestPrice: price,
    // The search API does not currently provide a verified public comparison.
    retailPrice: price,
    comparisonAvailable: false,
    amenities: hotel.amenities,
    searchOffer: hotel.offer
      ? {
          roomName: hotel.offer.roomName,
          mealName: hotel.offer.mealName,
          hasBreakfast: hotel.offer.hasBreakfast,
          totalPrice: hotel.offer.total
            ? { amount: String(withCardFee(hotel.offer.total.amount)), currencyCode: hotel.offer.total.currency }
            : null,
          freeCancellationBefore: openFreeCancellationBefore(hotel.offer.freeCancellationBefore),
          taxesIncluded: hotel.offer.taxesIncluded,
          paymentType: hotel.offer.paymentType,
        }
      : null,
    rates: [],
  };
}

export default function SearchScreen() {
  const params = useLocalSearchParams<{
    destination?: string;
    checkIn?: string;
    checkOut?: string;
    adults?: string;
    children?: string;
    rooms?: string;
    regionId?: string;
    region?: string;
    checkin?: string;
    checkout?: string;
    occ?: string;
    residency?: string;
    hotelId?: string;
    view?: string;
    page?: string;
    // Filters and sort, so a refresh, back button or shared link keeps them.
    stars?: string;
    free?: string;
    breakfast?: string;
    amenities?: string;
    minPrice?: string;
    maxPrice?: string;
    sort?: string;
  }>();
  const router = useRouter();
  const { width } = useWindowDimensions();
  const selectedCurrency = useCurrency((state) => state.selectedCurrency);
  const currencyRates = useCurrency((state) => state.rates);
  const desktop = width >= 1040;
  const compact = width < 700;
  const isMember = useMember((state) => state.isMember);
  const openPrompt = useMember((state) => state.openPrompt);
  const requestId = useRef(0);
  const searchIdRef = useRef<string | null>(null);
  const scrollRef = useRef<ScrollView>(null);

  const [search, setSearch] = useState<SearchBarValue>(() => paramsToSearchBarValue(params));
  const appliedResidency = params.residency === undefined ? getDefaultResidency() : normalizeResidency(params.residency) || getDefaultResidency();
  const nationalityPending = normalizeResidency(search.residency) !== appliedResidency;
  const [results, setResults] = useState<HotelSummary[]>([]);
  const [availableCount, setAvailableCount] = useState(0);
  const [loading, setLoading] = useState(true);
  // Appending the next page keeps the current results on screen.
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(() => Math.max(1, Number(params.page) || 1));
  const [totalPages, setTotalPages] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [searchId, setSearchId] = useState<string | null>(null);
  const [facets, setFacets] = useState<Api.Search.Hotels.Facets | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [minimumStars, setMinimumStars] = useState<number | null>(() => (STAR_FILTERS.includes(Number(params.stars)) ? Number(params.stars) : null));
  const [freeCancellationOnly, setFreeCancellationOnly] = useState(() => SHOW_FETCHED_ONLY_FILTERS && params.free === '1');
  const [breakfastOnly, setBreakfastOnly] = useState(() => params.breakfast === '1');
  const [selectedAmenities, setSelectedAmenities] = useState<AmenityKey[]>(() => (SHOW_FETCHED_ONLY_FILTERS ? params.amenities ?? '' : '').split(',').filter((key): key is AmenityKey => (AMENITY_KEYS as readonly string[]).includes(key)));
  const [minimumPrice, setMinimumPrice] = useState(() => priceParam(params.minPrice));
  const [maximumPrice, setMaximumPrice] = useState(() => priceParam(params.maxPrice));
  const [debouncedMinPrice, setDebouncedMinPrice] = useState(() => priceParam(params.minPrice));
  const [debouncedMaxPrice, setDebouncedMaxPrice] = useState(() => priceParam(params.maxPrice));
  const [sort, setSort] = useState<SortKey>(() => (SHOW_FETCHED_ONLY_FILTERS && SORT_KEYS.includes(params.sort as SortKey) ? (params.sort as SortKey) : 'recommended'));
  const [sortOpen, setSortOpen] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  // Debounce price input changes before triggering server requests
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedMinPrice(minimumPrice);
      if (minimumPrice !== '') {
        pendingFilterChangeRef.current = { filterType: 'minPrice', value: minimumPrice, countBefore: availableCount };
      }
    }, 400);
    return () => clearTimeout(handler);
  }, [minimumPrice]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedMaxPrice(maximumPrice);
      if (maximumPrice !== '') {
        pendingFilterChangeRef.current = { filterType: 'maxPrice', value: maximumPrice, countBefore: availableCount };
      }
    }, 400);
    return () => clearTimeout(handler);
  }, [maximumPrice]);

  const activeFilters = useMemo<Api.Search.Hotels.FilterCriteria>(() => {
    const min = debouncedMinPrice === '' ? undefined : Number(debouncedMinPrice);
    const max = debouncedMaxPrice === '' ? undefined : Number(debouncedMaxPrice);
    const criteria: Api.Search.Hotels.FilterCriteria = {};
    if (minimumStars !== null) criteria.minStars = minimumStars;
    if (freeCancellationOnly) criteria.freeCancellation = true;
    if (breakfastOnly) criteria.breakfast = true;
    if (selectedAmenities.length > 0) criteria.amenities = selectedAmenities;
    // The guest types prices in the currency they browse in; search prices
    // and the price filter are in USD. Rates are read here, not a dependency: a rates
    // refresh must never change the filters and re-run the search.
    const rates = useCurrency.getState().rates;
    if (min !== undefined && Number.isFinite(min) && min > 0) criteria.minPrice = withoutCardFee(convertPriceCeil(min, selectedCurrency, 'USD', rates));
    if (max !== undefined && Number.isFinite(max) && max > 0) criteria.maxPrice = withoutCardFee(convertPriceCeil(max, selectedCurrency, 'USD', rates));
    return criteria;
  }, [minimumStars, freeCancellationOnly, breakfastOnly, selectedAmenities, debouncedMinPrice, debouncedMaxPrice, selectedCurrency]);

  const resolvedRegionsRef = useRef<Record<string, { id: number; name: string }>>({});
  const inFlightKey = useRef<string | null>(null);
  const filtersMounted = useRef(false);
  const pendingFilterChangeRef = useRef<PendingFilterChange | null>(null);

  async function resolveDestination(query: string): Promise<{ id: number; name: string } | null> {
    const trimmed = query.trim();
    if (!trimmed) return null;

    const cacheKey = trimmed.toLowerCase();
    if (resolvedRegionsRef.current[cacheKey]) {
      return resolvedRegionsRef.current[cacheKey];
    }

    try {
      const destinationResponse = await api.search.destinations({ query: trimmed });
      const region = destinationResponse.data.regions?.[0];
      const hotel = destinationResponse.data.hotels?.[0];
      const id = region?.regionId ?? hotel?.regionId ?? null;
      const name = region?.name ?? hotel?.name ?? trimmed;

      if (id) {
        const item = { id, name };
        resolvedRegionsRef.current[cacheKey] = item;
        return item;
      }
    } catch {
      // ignore
    }

    return null;
  }

  async function performSearch(
    targetSearch: SearchBarValue,
    filtersToApply: Api.Search.Hotels.FilterCriteria,
    sortToApply: SortKey,
    pageToFetch = 1,
    append = false,
    sid: string | null = searchIdRef.current,
    explicitRegionId?: number,
    emitSubmitted = false
  ) {
    const selectedResidency = normalizeResidency(targetSearch.residency);
    if (!selectedResidency) {
      setLoading(false);
      setResults([]);
      setAvailableCount(0);
      setError('Choose the lead guest’s nationality in the search bar to see accurate rates.');
      return;
    }
    const searchKey = `${targetSearch.destination}_${targetSearch.dates.start}_${targetSearch.dates.end}_${selectedResidency}_${JSON.stringify(targetSearch.guests)}_${pageToFetch}_${JSON.stringify(filtersToApply)}_${sortToApply}_${sid ?? ''}_${explicitRegionId ?? ''}`;
    if (!append && inFlightKey.current === searchKey) {
      return;
    }
    inFlightKey.current = searchKey;

    const currentRequest = ++requestId.current;
    const requestStartedAt = Date.now();
    const pendingFilter = pendingFilterChangeRef.current;
    pendingFilterChangeRef.current = null;
    const targetNights = targetSearch.dates.start && targetSearch.dates.end
      ? Math.max(1, Math.round((new Date(`${targetSearch.dates.end}T00:00:00Z`).getTime() - new Date(`${targetSearch.dates.start}T00:00:00Z`).getTime()) / 86_400_000))
      : 1;

    function announceResults(resultCount: number, catalogueCount: number | null, errorCode: string | null, resolvedSearchId = sid) {
      if (append) return;
      const effectiveSearchId = resolvedSearchId ?? '';
      track('search.results_returned', {
        searchId: effectiveSearchId,
        resultCount,
        catalogueCount,
        page: pageToFetch,
        latencyMs: Date.now() - requestStartedAt,
        errorCode,
      });
      if (pendingFilter) {
        track('search.filter_applied', {
          searchId: effectiveSearchId,
          filterType: pendingFilter.filterType,
          value: pendingFilter.value,
          countBefore: pendingFilter.countBefore,
          countAfter: resultCount,
        });
      }
    }

    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setError(null);
    }

    try {
      let regionId = explicitRegionId;

      if (!regionId) {
        const destQuery = targetSearch.destination.trim();
        // Only trust params.regionId if destQuery matches params.region or params.destination
        const isParamMatching = params.regionId && (
          !destQuery ||
          (params.region && params.region.toLowerCase() === destQuery.toLowerCase()) ||
          (params.destination && params.destination.toLowerCase() === destQuery.toLowerCase())
        );

        if (isParamMatching) {
          regionId = Number(params.regionId);
        } else if (destQuery) {
          const resolved = await resolveDestination(destQuery);
          if (resolved) {
            regionId = resolved.id;
            if (String(resolved.id) !== params.regionId || resolved.name !== params.region) {
              router.setParams({
                regionId: String(resolved.id),
                region: resolved.name,
                destination: destQuery,
              });
            }
          }
        }
      }

      if (!regionId) {
        if (!append) {
          setResults([]);
          setAvailableCount(0);
          setTotalPages(1);
          setHasMore(false);
          const destQuery = targetSearch.destination.trim();
          setError(`No destinations found for "${destQuery || 'this location'}". Try a different city, or check the spelling.`);
        }
        if (emitSubmitted) {
          track('search.submitted', {
            regionId: 0,
            regionName: null,
            checkin: targetSearch.dates.start ?? '',
            checkout: targetSearch.dates.end ?? '',
            nights: targetNights,
            guests: targetSearch.guests.rooms,
            adults: targetSearch.guests.adults,
            children: targetSearch.guests.children,
            residency: selectedResidency,
            currency: 'USD',
            leadTimeDays: leadTimeDaysFrom(targetSearch.dates.start),
          });
        }
        announceResults(0, null, 'destination_not_found');
        setLoading(false);
        inFlightKey.current = null;
        return;
      }

      if (emitSubmitted) {
        track('search.submitted', {
          regionId,
          regionName: params.region || null,
          checkin: targetSearch.dates.start ?? '',
          checkout: targetSearch.dates.end ?? '',
          nights: targetNights,
          guests: targetSearch.guests.rooms,
          adults: targetSearch.guests.adults,
          children: targetSearch.guests.children,
          residency: selectedResidency,
          currency: 'USD',
          leadTimeDays: leadTimeDaysFrom(targetSearch.dates.start),
        });
      }

      const response = await api.search.hotels({
        regionId,
        checkin: targetSearch.dates.start ?? '',
        checkout: targetSearch.dates.end ?? '',
        guests: toEtgGuests(targetSearch.guests),
        residency: selectedResidency,
        page: pageToFetch,
        limit: RESULTS_PER_PAGE,
        searchId: sid ?? undefined,
        filters: Object.keys(filtersToApply).length > 0 ? filtersToApply : undefined,
        sort: sortToApply,
      });

      if (response.data.error) throw new Error(response.data.error);
      if (currentRequest !== requestId.current) return;

      const newHotels = (response.data.hotels ?? []).map(toHotelSummary);
      if (append) {
        // A request past the last page returns that page again: keep only new hotels.
        setResults((prev) => {
          const shown = new Set(prev.map((hotel) => hotel.id));
          return [...prev, ...newHotels.filter((hotel) => !shown.has(hotel.id))];
        });
      } else {
        setResults(newHotels);
      }

      const total = response.data.pagination?.total ?? response.data.available ?? newHotels.length;
      const pagesCount = response.data.pagination?.totalPages ?? Math.max(1, Math.ceil(total / RESULTS_PER_PAGE));
      setAvailableCount(total);
      setPage(response.data.pagination?.page ?? pageToFetch);
      setTotalPages(pagesCount);
      setHasMore(Boolean(response.data.pagination?.hasMore));

      const nextSearchId = response.data.pagination?.searchId ?? null;
      searchIdRef.current = nextSearchId;
      setSearchId(nextSearchId);

      if (response.data.facets) {
        setFacets(response.data.facets);
      }

      announceResults(total, response.data.catalogue ?? null, null, nextSearchId);
    } catch (err) {
      if (currentRequest !== requestId.current) return;
      if (!append) {
        setResults([]);
        setAvailableCount(0);
        setTotalPages(1);
        setHasMore(false);
        setError('The live rates service did not complete this search.');
      }
      announceResults(0, null, err instanceof Error && err.message ? err.message : 'search_failed');
    } finally {
      inFlightKey.current = null;
      if (currentRequest === requestId.current) {
        setLoading(false);
        setLoadingMore(false);
      }
      if (!append) {
        scrollToTop();
        if (typeof requestAnimationFrame !== 'undefined') {
          requestAnimationFrame(() => {
            scrollToTop();
          });
        }
      }
    }
  }

  function scrollToTop(smooth = false) {
    if (scrollRef.current) {
      try {
        if (typeof scrollRef.current.scrollTo === 'function') {
          scrollRef.current.scrollTo({ y: 0, animated: smooth });
        }
      } catch {}
      try {
        const node = (scrollRef.current as any).getScrollableNode?.() ?? scrollRef.current;
        if (node && typeof node === 'object') {
          if (typeof node.scrollTo === 'function') {
            node.scrollTo({ top: 0, left: 0, behavior: smooth ? 'smooth' : 'auto' });
          }
          if ('scrollTop' in node) {
            node.scrollTop = 0;
          }
        }
      } catch {}
    }

    if (typeof document !== 'undefined') {
      const container = document.getElementById('screen-scroll-container');
      if (container) {
        try {
          container.scrollTo({ top: 0, left: 0, behavior: smooth ? 'smooth' : 'auto' });
        } catch {}
        container.scrollTop = 0;
      }
      if (document.documentElement) document.documentElement.scrollTop = 0;
      if (document.body) document.body.scrollTop = 0;
      const root = document.getElementById('root');
      if (root) {
        try {
          root.scrollTo({ top: 0, left: 0, behavior: smooth ? 'smooth' : 'auto' });
        } catch {}
        root.scrollTop = 0;
      }
    }
    if (typeof window !== 'undefined') {
      try {
        window.scrollTo({ top: 0, left: 0, behavior: smooth ? 'smooth' : 'auto' });
      } catch {}
    }
  }

  // Numbered pages are switched off for now in favour of infinite loading
  // (loadMore below). To bring them back, restore this handler and the
  // <Pagination> block in the results list.
  // async function handlePageChange(targetPage: number) {
  //   if (targetPage === page || targetPage < 1 || targetPage > totalPages || loading) return;
  //
  //   scrollToTop();
  //
  //   router.setParams({ page: String(targetPage) });
  //   track('search.page_viewed', { searchId: searchIdRef.current ?? '', page: targetPage });
  //
  //   await performSearch(search, activeFilters, sort, targetPage, false, searchIdRef.current);
  //   scrollToTop();
  // }

  // Infinite loading: near the end of the list, append the next page. The
  // server fetches more hotels from the supplier when it has none left.
  function loadMore() {
    // inFlightKey is set synchronously, so scroll events arriving before the
    // loading state re-renders cannot request the same page twice.
    if (loading || loadingMore || inFlightKey.current || error || page >= totalPages || !searchIdRef.current) return;
    track('search.page_viewed', { searchId: searchIdRef.current, page: page + 1 });
    void performSearch(search, activeFilters, sort, page + 1, true, searchIdRef.current);
  }

  function handleScroll({ nativeEvent }: NativeSyntheticEvent<NativeScrollEvent>) {
    const { contentOffset, layoutMeasurement, contentSize } = nativeEvent;
    if (contentOffset.y + layoutMeasurement.height >= contentSize.height - LOAD_MORE_THRESHOLD_PX) {
      loadMore();
    }
  }

  // Initial mount & URL parameter changes (fresh search session)
  useEffect(() => {
    const nextValue = paramsToSearchBarValue(params);
    setSearch(nextValue);
    searchIdRef.current = null;
    setSearchId(null);
    // Infinite loading always starts at the top; with numbered pages this was
    // Math.max(1, Number(params.page) || 1).
    const targetPage = 1;
    void performSearch(nextValue, activeFilters, sort, targetPage, false, null, undefined, true);
  }, [params.destination, params.region, params.regionId, params.checkIn, params.checkOut, params.checkin, params.checkout, params.adults, params.children, params.rooms, params.occ, params.residency]);

  // Synchronize with browser back/forward history navigation
  useEffect(() => {
    const pageFromUrl = Math.max(1, Number(params.page) || 1);
    if (pageFromUrl !== page && !loading && searchIdRef.current) {
      scrollToTop();
      track('search.page_viewed', { searchId: searchIdRef.current, page: pageFromUrl });
      void performSearch(search, activeFilters, sort, pageFromUrl, false, searchIdRef.current);
    }
  }, [params.page]);

  // Filter or sort changes on the active search session (skip on initial mount)
  useEffect(() => {
    if (!filtersMounted.current) {
      filtersMounted.current = true;
      return;
    }
    // undefined drops a param, so default filters leave a clean URL.
    router.setParams({
      page: '1',
      stars: minimumStars === null ? undefined : String(minimumStars),
      free: freeCancellationOnly ? '1' : undefined,
      breakfast: breakfastOnly ? '1' : undefined,
      amenities: selectedAmenities.length > 0 ? selectedAmenities.join(',') : undefined,
      minPrice: debouncedMinPrice || undefined,
      maxPrice: debouncedMaxPrice || undefined,
      sort: sort === 'recommended' ? undefined : sort,
    });
    void performSearch(search, activeFilters, sort, 1, false, searchIdRef.current);
  }, [activeFilters, sort]);

  function handleSelectPlace(place: Place, currentSearch: SearchBarValue) {
    setSearch(currentSearch);
  }

  function updateSearch(nextSearch: SearchBarValue) {
    if (nextSearch.residency !== search.residency) {
      requestId.current += 1;
      inFlightKey.current = null;
      searchIdRef.current = null;
      setSearchId(null);
      setResults([]);
      setAvailableCount(0);
      setLoading(false);
      setError(null);
    }
    setSearch(nextSearch);
  }

  async function handleSearch() {
    if (!normalizeResidency(search.residency)) return;
    trackAmplitude('Hotel Searched');

    const destQuery = search.destination.trim();
    const common = {
      checkin: search.dates.start ?? '',
      checkout: search.dates.end ?? '',
      occ: encodeOccupancy(toEtgGuests(search.guests)),
      residency: search.residency,
    };

    if (search.selectedDestination?.kind === 'hotel') {
      const hid = search.selectedDestination.hid ?? (search.selectedDestination.id ? Number(search.selectedDestination.id) : null);
      if (hid) {
        router.push({
          pathname: '/hotel/[id]',
          params: {
            id: String(hid),
            ...common,
          },
        });
        return;
      }
    }

    // A picked city is searched as that city, never as a hotel sharing its name.
    const pickedCity = search.selectedDestination?.kind === 'city';
    const matchedHotel = pickedCity ? undefined : findHotelPlace(destQuery);
    if (matchedHotel?.hid) {
      router.push({
        pathname: '/hotel/[id]',
        params: {
          id: String(matchedHotel.hid),
          ...common,
        },
      });
      return;
    }

    try {
      const exactHotel = pickedCity ? undefined : exactHotelMatch((await api.search.destinations({ query: destQuery })).data, destQuery);
      if (exactHotel) {
        router.push({
          pathname: '/hotel/[id]',
          params: {
            id: String(exactHotel.hid),
            ...common,
          },
        });
        return;
      }
    } catch {}

    searchIdRef.current = null;
    setSearchId(null);
    setLoading(true);
    setError(null);

    let resolvedId: number | null = null;
    let resolvedName = destQuery || 'All stays';

    if (search.selectedDestination?.kind === 'city' && search.selectedDestination.id) {
      resolvedId = Number(search.selectedDestination.id);
      resolvedName = search.selectedDestination.label;
    } else if (destQuery) {
      const resolved = await resolveDestination(destQuery);
      if (resolved) {
        resolvedId = resolved.id;
        resolvedName = resolved.name;
      }
    }

    if (!resolvedId) {
      setResults([]);
      setAvailableCount(0);
      setTotalPages(1);
      setHasMore(false);
      setError(`No destinations found for "${destQuery}". Try a different city, or check the spelling.`);
      setLoading(false);
      return;
    }

    router.setParams({
      destination: destQuery,
      region: resolvedName,
      regionId: String(resolvedId),
      checkin: search.dates.start ?? '',
      checkout: search.dates.end ?? '',
      occ: encodeOccupancy(toEtgGuests(search.guests)),
      residency: search.residency,
      page: '1',
    });

    void performSearch(search, activeFilters, sort, 1, false, null, resolvedId);
  }

  // Picking a destination from the expanded search's popular panel starts a
  // fresh search — the previous region id must not carry over.
  function handleSelectDestination(label: string) {
    router.setParams({
      destination: label,
      region: label,
      regionId: '',
      checkin: search.dates.start ?? '',
      checkout: search.dates.end ?? '',
      occ: encodeOccupancy(toEtgGuests(search.guests)),
      residency: search.residency,
    });
  }

  function clearFilters() {
    setMinimumStars(null);
    setFreeCancellationOnly(false);
    setBreakfastOnly(false);
    setSelectedAmenities([]);
    setMinimumPrice('');
    setMaximumPrice('');
    setDebouncedMinPrice('');
    setDebouncedMaxPrice('');
  }

  const anyFilterOn = minimumStars !== null || freeCancellationOnly || breakfastOnly || selectedAmenities.length > 0 || minimumPrice !== '' || maximumPrice !== '';
  const sortOptions = BASE_SORT_OPTIONS;

  const filterCounts = useMemo(() => ({
    stars: {
      5: facets?.stars?.[5] ?? 0,
      4: facets?.stars?.[4] ?? 0,
      3: facets?.stars?.[3] ?? 0,
      2: facets?.stars?.[2] ?? 0,
    },
    freeCancellation: facets?.freeCancellation ?? 0,
    breakfast: facets?.breakfast ?? 0,
    amenities: Object.fromEntries(AMENITY_FILTERS.map((filter) => [filter.key, facets?.amenities?.[filter.key] ?? 0])),
  }), [facets]);

  function toggleAmenity(key: AmenityKey, checked: boolean) {
    if (checked) {
      pendingFilterChangeRef.current = { filterType: 'amenities', value: key, countBefore: availableCount };
    }
    setSelectedAmenities((current) => checked ? [...current, key] : current.filter((item) => item !== key));
  }

  function applyStarFilter(stars: number, checked: boolean) {
    if (checked) {
      pendingFilterChangeRef.current = { filterType: 'minStars', value: String(stars), countBefore: availableCount };
    }
    setMinimumStars(checked ? stars : null);
  }

  function applyFreeCancellationFilter(checked: boolean) {
    if (checked) {
      pendingFilterChangeRef.current = { filterType: 'freeCancellation', value: 'true', countBefore: availableCount };
    }
    setFreeCancellationOnly(checked);
  }

  function applyBreakfastFilter(checked: boolean) {
    if (checked) {
      pendingFilterChangeRef.current = { filterType: 'breakfast', value: 'true', countBefore: availableCount };
    }
    setBreakfastOnly(checked);
  }

  const stayLabel = formatRangeLabel(search.dates.start, search.dates.end);
  const guestLabel = formatGuestsLabel(search.guests);
  // Result copy reflects the submitted search, never the in-progress text in
  // the expanded search box.
  const destinationLabel = params.region?.trim() || params.destination?.trim() || 'All stays';
  const numberOfNights = search.dates.start && search.dates.end
    ? Math.max(1, Math.round((new Date(`${search.dates.end}T00:00:00Z`).getTime() - new Date(`${search.dates.start}T00:00:00Z`).getTime()) / 86_400_000))
    : 1;
  const navTriggerLabel = `${destinationLabel} · ${stayLabel} · ${guestLabel}`;
  // Distinct summary groups for the collapsed nav pill.
  const summaryDates = formatRangeLabelCompact(search.dates.start, search.dates.end);
  const summaryGuestCount = search.guests.adults + search.guests.children;
  const summaryGuests = `${summaryGuestCount} ${summaryGuestCount === 1 ? 'guest' : 'guests'} · ${search.guests.rooms} ${search.guests.rooms === 1 ? 'room' : 'rooms'}`;
  // Direct hotel selections route to /hotel/[id]; only region-led searches reach this page.
  const isCitySearch = !params.hotelId;
  const activeSort = sortOptions.find((option) => option.value === sort) ?? sortOptions[0];

  // rank is the hotel's 1-based position across the full result set (not
  // just this page) — it cannot be reconstructed later since the search
  // session expires, so every navigation away from a result must carry it.
  function rankOf(hotel: HotelSummary): number {
    // Results are one appended list, so its index is the rank (with numbered
    // pages this was (page - 1) * RESULTS_PER_PAGE + indexOnPage + 1).
    const indexOnPage = results.findIndex((item) => item.id === hotel.id);
    return indexOnPage < 0 ? 0 : indexOnPage + 1;
  }

  function openHotel(hotel: HotelSummary, rank: number = rankOf(hotel)) {
    track('search.result_clicked', {
      searchId: searchIdRef.current ?? '',
      hid: hotel.hid,
      rank,
      page,
      minPrice: Number(hotel.lowestPrice.amount) || 0,
      starRating: hotel.starRating,
      hasFreeCancellation: Boolean(hotel.searchOffer?.freeCancellationBefore),
      hasBreakfast: Boolean(hotel.searchOffer?.hasBreakfast),
    });
    router.push({
      pathname: '/hotel/[id]',
      params: {
        id: String(hotel.hid),
        checkin: search.dates.start ?? '',
        checkout: search.dates.end ?? '',
        occ: encodeOccupancy(toEtgGuests(search.guests)),
        residency: search.residency,
        source: 'search',
        searchId: searchIdRef.current ?? '',
      },
    });
  }

  const standoutDeals = !loading && !error && isCitySearch && results.length > 0 ? (
      <CityStandoutDeals
        city={destinationLabel}
        hotels={results}
        onSelect={openHotel}
        getHref={(hotel) =>
          buildHotelHref({
            id: hotel.hid,
            checkin: search.dates.start ?? '',
            checkout: search.dates.end ?? '',
            occ: encodeOccupancy(toEtgGuests(search.guests)),
            residency: search.residency,
          })
        }
      />
    ) : null;

  const filters = (
    <View style={styles.filterCard}>
      {anyFilterOn ? <Pressable accessibilityRole="button" onPress={clearFilters} style={styles.clearFiltersButton}><Text style={styles.clearLink}>Clear all filters</Text></Pressable> : null}
      <FilterSection title="Price per night">
        <View style={styles.priceInputs}>
          <View style={styles.priceInputBox}>
            <Text style={styles.inputLabel}>Min</Text>
            <TextInput
              accessibilityLabel="Minimum nightly price"
              value={minimumPrice}
              onChangeText={setMinimumPrice}
              keyboardType="decimal-pad"
              placeholder={formatCurrency(facets?.priceRange?.min ? convertPriceCeil(facets.priceRange.min, 'USD', selectedCurrency, currencyRates) : 0, selectedCurrency)}
              placeholderTextColor={colors.inkFaint}
              style={styles.priceInput}
            />
          </View>
          <View style={styles.priceInputBox}>
            <Text style={styles.inputLabel}>Max</Text>
            <TextInput
              accessibilityLabel="Maximum nightly price"
              value={maximumPrice}
              onChangeText={setMaximumPrice}
              keyboardType="decimal-pad"
              placeholder={facets?.priceRange?.max ? formatCurrency(convertPriceCeil(facets.priceRange.max, 'USD', selectedCurrency, currencyRates), selectedCurrency) : 'Any'}
              placeholderTextColor={colors.inkFaint}
              style={styles.priceInput}
            />
          </View>
        </View>
      </FilterSection>
      <FilterSection title="Star rating">
        <Checkbox label="5 stars" count={SHOW_FETCHED_ONLY_FILTERS ? filterCounts.stars[5] : undefined} checked={minimumStars === 5} onChange={(checked) => applyStarFilter(5, checked)} />
        <Checkbox label="4 stars and up" count={SHOW_FETCHED_ONLY_FILTERS ? filterCounts.stars[4] : undefined} checked={minimumStars === 4} onChange={(checked) => applyStarFilter(4, checked)} />
        <Checkbox label="3 stars and up" count={SHOW_FETCHED_ONLY_FILTERS ? filterCounts.stars[3] : undefined} checked={minimumStars === 3} onChange={(checked) => applyStarFilter(3, checked)} />
        <Checkbox label="2 stars and up" count={SHOW_FETCHED_ONLY_FILTERS ? filterCounts.stars[2] : undefined} checked={minimumStars === 2} onChange={(checked) => applyStarFilter(2, checked)} />
      </FilterSection>
      <FilterSection title="Booking">
        {SHOW_FETCHED_ONLY_FILTERS ? <Checkbox label="Free cancellation" count={filterCounts.freeCancellation} checked={freeCancellationOnly} onChange={applyFreeCancellationFilter} /> : null}
        <Checkbox label="Breakfast included" count={SHOW_FETCHED_ONLY_FILTERS ? filterCounts.breakfast : undefined} checked={breakfastOnly} onChange={applyBreakfastFilter} />
      </FilterSection>
      {SHOW_FETCHED_ONLY_FILTERS ? <FilterSection title="Amenities">
        {AMENITY_FILTERS.map((filter) => (
          <Checkbox
            key={filter.key}
            label={filter.label}
            icon={filter.icon}
            count={filterCounts.amenities[filter.key]}
            checked={selectedAmenities.includes(filter.key)}
            onChange={(checked) => toggleAmenity(filter.key, checked)}
          />
        ))}
      </FilterSection> : null}
    </View>
  );

  const searchPlaceLabel = params.destination || params.region || 'hotels';

  return (
    <Screen scrollRef={scrollRef} onScroll={handleScroll}>
      <Head>
        <title>{`Search ${searchPlaceLabel} — SuiteScanner`}</title>
        <meta
          name="description"
          content={`Compare direct hotel prices in ${searchPlaceLabel} — same hotel, same room, no commission markup. Sign in for the member-only SuiteScanner+ rate.`}
        />
      </Head>
      <NavSearchBar
        value={search}
        onChange={updateSearch}
        onSearch={handleSearch}
        onSelectDestination={handleSelectDestination}
        onSelectPlace={handleSelectPlace}
        triggerLabel={navTriggerLabel}
        summary={{ destination: destinationLabel, dates: summaryDates, guests: summaryGuests }}
        autoExpand={!search.residency}
        sticky
        triggerAccessibilityLabel={`Search: ${navTriggerLabel}. Edit search`}
      />

      <View style={[styles.content, compact && styles.contentCompact]}>
        {/* On phones the deals carousel follows the first few results (below):
            above them it filled half the first screen. */}
        {loading && isCitySearch && !compact ? <CityStandoutDealsSkeleton /> : null}
        {!compact ? standoutDeals : null}
        <View style={[styles.resultsHeader, compact && styles.resultsHeaderCompact]}>
          <View style={styles.resultsTitleGroup}>
            <Text style={styles.resultsTitle}>{loading ? 'Finding stays…' : !search.residency ? 'Choose nationality to see stays' : nationalityPending ? 'Search to see updated rates' : `Stays in ${destinationLabel}`}</Text>
            <Text style={styles.resultsContext}>{stayLabel} · {guestLabel}</Text>
          </View>
          <View style={styles.controls}>
            {!desktop ? (
              <Pressable accessibilityRole="button" accessibilityState={{ expanded: mobileFiltersOpen }} onPress={() => setMobileFiltersOpen((open) => !open)} style={styles.filterButton}>
                <Ionicons name="options-outline" size={16} color={colors.inkSoft} />
                <Text style={styles.filterButtonText}>Filters{anyFilterOn ? ' · On' : ''}</Text>
              </Pressable>
            ) : null}
            {SHOW_FETCHED_ONLY_FILTERS ? <View style={styles.sortAnchor}>
              <Pressable accessibilityRole="button" accessibilityLabel="Change sort order" accessibilityState={{ expanded: sortOpen }} onPress={() => setSortOpen((open) => !open)} style={styles.sortButton}>
                <Text style={styles.sortText}>{compact ? activeSort.label : `Sort by: ${activeSort.label}`}</Text>
                <Ionicons name={sortOpen ? 'chevron-up' : 'chevron-down'} size={14} color={colors.inkSoft} />
              </Pressable>
              {sortOpen ? (
                <View style={styles.sortMenu}>
                  {sortOptions.map((option) => (
                    <Pressable key={option.value} accessibilityRole="button" accessibilityState={{ selected: sort === option.value }} onPress={() => { if (searchIdRef.current) track('search.sort_changed', { searchId: searchIdRef.current, sort: toContractSort(option.value) }); setSort(option.value); setSortOpen(false); }} style={styles.sortOption}>
                      <Text style={[styles.sortOptionText, sort === option.value && styles.sortOptionTextActive]}>{option.label}</Text>
                      {sort === option.value ? <Ionicons name="checkmark" size={15} color={colors.brand} /> : null}
                    </Pressable>
                  ))}
                </View>
              ) : null}
            </View> : null}
          </View>
        </View>

        {!desktop && !compact && mobileFiltersOpen ? <View style={styles.mobileFilters}>{filters}</View> : null}

        <View style={[styles.layout, desktop && styles.layoutDesktop]}>
          {desktop ? <View style={styles.aside}>{filters}</View> : null}
          <View style={styles.main}>
            {loading ? (
              <SearchResultsSkeleton />
            ) : nationalityPending ? (
              <EmptyState icon="globe-outline" title="Search again for updated rates" subtitle="Rates can change with the lead guest’s nationality. Select Search hotels above to see prices for this citizenship." />
            ) : error ? (
              !search.residency ? <EmptyState icon="globe-outline" title="Choose the lead guest’s nationality" subtitle="Open the search bar above and select the lead guest’s country of citizenship to see rates for this stay." /> :
              <EmptyState icon="alert-circle-outline" title="We couldn’t load these stays" subtitle={error} actionLabel="Retry" onAction={() => void performSearch(search, activeFilters, sort, 1, false, searchIdRef.current)} />
            ) : results.length === 0 ? (
              <EmptyState icon="search" title="No stays match these filters" subtitle={anyFilterOn ? 'Clear your filters to see more available stays.' : 'Try changing your destination or dates.'} actionLabel={anyFilterOn ? 'Clear filters' : 'Change search'} onAction={anyFilterOn ? clearFilters : () => router.push('/')} />
            ) : (
              <View style={styles.list}>
                {results.map((hotel, index) => (
                  <Fragment key={hotel.id}>
                  <HotelRow
                    hotel={hotel}
                    isMember={isMember}
                    onUnlock={openPrompt}
                    nights={numberOfNights}
                    adults={search.guests.adults}
                    onPress={() => openHotel(hotel, index + 1)}
                    href={buildHotelHref({
                      id: hotel.hid,
                      checkin: search.dates.start ?? '',
                      checkout: search.dates.end ?? '',
                      occ: encodeOccupancy(toEtgGuests(search.guests)),
                      residency: search.residency,
                    })}
                  />
                  {compact && index === Math.min(2, results.length - 1) ? standoutDeals : null}
                  </Fragment>
                ))}
                {/* Numbered pages are off while results load infinitely; see handlePageChange.
                <Pagination
                  currentPage={page}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  disabled={loading}
                /> */}
                {loadingMore ? <ActivityIndicator accessibilityLabel="Loading more stays" color={colors.brand} style={styles.loadingMore} /> : null}
              </View>
            )}
          </View>
        </View>
      </View>

      <ModalSheet visible={compact && mobileFiltersOpen} onClose={() => setMobileFiltersOpen(false)}>
        <View style={styles.mobileFilterSheetHeader}>
          <Text style={styles.mobileFilterSheetTitle}>Filter stays</Text>
          <Pressable accessibilityRole="button" accessibilityLabel="Close filters" onPress={() => setMobileFiltersOpen(false)} style={styles.mobileFilterClose}>
            <Ionicons name="close" size={20} color={colors.inkSoft} />
          </Pressable>
        </View>
        <ScrollView contentContainerStyle={styles.mobileFilterScroll}>{filters}</ScrollView>
        <View style={styles.mobileFilterFooter}>
          <Pressable accessibilityRole="button" onPress={() => setMobileFiltersOpen(false)} style={styles.showResultsButton}>
            <Text style={styles.showResultsText}>Show stays</Text>
          </Pressable>
        </View>
      </ModalSheet>
    </Screen>
  );
}

function FilterSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <View style={styles.filterSection}>
      <Text style={styles.filterSectionTitle}>{title}</Text>
      <View style={styles.filterOptions}>{children}</View>
    </View>
  );
}

const styles = StyleSheet.create({
  loadingMore: { paddingVertical: space.cardLarge },
  content: { maxWidth: maxContentWidth, width: '100%', alignSelf: 'center', paddingHorizontal: space.card, paddingTop: space.cardLarge, paddingBottom: space.sectionMajor, gap: space.internal, boxSizing: 'border-box' as never },
  contentCompact: { width: 'auto', alignSelf: 'stretch', paddingHorizontal: spacing(2) },
  resultsHeader: { minHeight: 54, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: space.internal, position: 'relative', zIndex: 100 },
  resultsHeaderCompact: { alignItems: 'flex-start', flexDirection: 'column' },
  resultsTitleGroup: { gap: space.related },
  resultsTitle: { fontFamily: fontFamily.bold, ...type.sectionTitle, color: colors.ink },
  resultsContext: { fontFamily: fontFamily.regular, ...type.bodyRegular, color: colors.inkFaint },
  controls: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: space.related },
  filterButton: { minHeight: 42, flexDirection: 'row', alignItems: 'center', gap: space.related, paddingHorizontal: space.compact, borderWidth: 1, borderColor: colors.line, borderRadius: radii.sm, backgroundColor: colors.card },
  filterButtonText: { fontFamily: fontFamily.medium, fontSize: control.secondaryCta.fontSize, color: colors.inkSoft },
  sortAnchor: { position: 'relative', zIndex: 150 },
  sortButton: { minHeight: 42, minWidth: 188, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: space.related, paddingHorizontal: space.compact, borderWidth: 1, borderColor: colors.line, borderRadius: radii.sm, backgroundColor: colors.card },
  sortText: { fontFamily: fontFamily.medium, fontSize: control.secondaryCta.fontSize, color: colors.inkSoft },
  sortMenu: { position: 'absolute', right: 0, top: 48, width: 220, padding: space.related, backgroundColor: colors.card, borderWidth: 1, borderColor: colors.line, borderRadius: radii.sm, ...shadows.card },
  sortOption: { minHeight: 40, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: space.related, paddingHorizontal: space.compact, borderRadius: radii.sm },
  sortOptionText: { fontFamily: fontFamily.regular, fontSize: type.bodyRegular.fontSize, color: colors.inkSoft },
  sortOptionTextActive: { fontFamily: fontFamily.semibold, color: colors.brand },
  layout: { flexDirection: 'column', gap: space.internal },
  layoutDesktop: { flexDirection: 'row', alignItems: 'flex-start' },
  aside: { width: 252, flexShrink: 0 },
  main: { flex: 1, minWidth: 0 },
  list: { gap: space.internal },
  mobileFilters: { position: 'relative', zIndex: 50 },
  mobileFilterSheetHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: space.internal, paddingVertical: space.compact, borderBottomWidth: 1, borderBottomColor: colors.line },
  mobileFilterSheetTitle: { fontFamily: fontFamily.bold, fontSize: type.subsectionTitle.fontSize, color: colors.ink },
  mobileFilterClose: { width: 36, height: 36, alignItems: 'center', justifyContent: 'center', borderRadius: radii.full },
  mobileFilterScroll: { padding: space.internal },
  mobileFilterFooter: { padding: space.internal, borderTopWidth: 1, borderTopColor: colors.line },
  showResultsButton: { minHeight: 46, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.brand, borderRadius: radii.sm },
  showResultsText: { fontFamily: fontFamily.semibold, fontSize: control.primaryCta.fontSize, color: colors.white },
  // Utility panel: a soft hairline border and lighter labels so filters never
  // compete with the hotel results. Hierarchy comes from the spacing between
  // groups, not from heavier type.
  filterCard: { backgroundColor: colors.card, borderWidth: 1, borderColor: colors.lineSoft, borderRadius: radii.md, padding: space.internal, gap: 0, ...shadows.sm },
  clearFiltersButton: { alignSelf: 'flex-end', paddingBottom: space.compact },
  clearLink: { fontFamily: fontFamily.regular, fontSize: control.utilityCta.fontSize, color: colors.brand, textDecorationLine: 'underline' },
  filterSection: { gap: space.related, paddingVertical: space.internal, borderTopWidth: 1, borderTopColor: colors.lineSoft },
  filterSectionTitle: { fontFamily: fontFamily.medium, fontSize: type.bodyRegular.fontSize, color: colors.inkSoft },
  filterOptions: { gap: space.related },
  priceInputs: { flexDirection: 'row', gap: space.related },
  priceInputBox: { flex: 1, minWidth: 0, borderWidth: 1, borderColor: colors.line, borderRadius: radii.sm, paddingVertical: space.related, paddingHorizontal: space.related },
  inputLabel: { fontFamily: fontFamily.regular, fontSize: control.inputLabel.fontSize, color: colors.inkFaint },
  priceInput: { minHeight: 24, padding: 0, fontFamily: fontFamily.medium, fontSize: control.inputValue.fontSize, color: colors.ink, outlineStyle: 'none' as never },
});
